import {
  ActionIcon,
  Box,
  Menu,
  Text,
  Tooltip,
  createStyles,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import { removeNote } from "../../services/note";
import {
  formatDate,
  formatTime,
  getOptions,
  noteType_Client,
} from "../../utils";
import moment from "moment";
import { getAllFacilitiesModal } from "../../services";
import MenuDropdown from "./MenuDropdown";
import { TimeInput } from "@mantine/dates";
import { BiDotsVerticalRounded } from "react-icons/bi";
import {
  IconEye,
  IconPencil,
  IconCopy,
  IconFileText,
  IconTrash,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import SingleNotePdfModal from "../SingleNotePdfModal";

const useStyles = createStyles((theme) => ({
  cell: {
    background: "var(--app-surface-2)",
    borderRadius: 5,
    padding: 5,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
  parametersContainer: {
    background: "var(--app-surface-2)",
    borderRadius: 5,
    padding: 5,
    width: 50,
    color: "var(--app-text-muted)",
    fontSize: 14,
  },
  bg1: {
    "& td": {
      background: "var(--app-surface) !important",
      borderBottom: "1px solid var(--app-border) !important",
      padding: "13px 16px !important",
      fontSize: "13.5px !important",
    },
    cursor: "pointer",
  },
  bg2: {
    "& td": {
      background: "var(--app-surface) !important",
      borderBottom: "1px solid var(--app-border) !important",
      padding: "13px 16px !important",
      fontSize: "13.5px !important",
    },
    cursor: "pointer",
  },
  textDark: {
    "&&": {
      color: "var(--app-text) !important",
    },
  },
  textMuted: {
    "&&": {
      color: "var(--app-text-muted) !important",
    },
  },
  staffOverflow: {
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 5,
    padding: "1px 6px",
    fontSize: 11.5,
    color: "var(--app-text-muted)",
  },
  kebab: {
    "&&": {
      width: "30px !important",
      height: "30px !important",
      minHeight: "30px !important",
      minWidth: "30px !important",
      borderRadius: "8px !important",
      border: "1px solid transparent !important",
      background: "none !important",
      color: "var(--app-text-faint, #8A93A2) !important",
      cursor: "pointer",
      display: "grid",
      placeItems: "center",
      "&:hover": {
        background: "var(--app-surface-2) !important",
        borderColor: "var(--app-border) !important",
        color: "var(--app-text) !important",
      },
    },
  },
}));

const NoteTableRow = ({
  row,
  index,
  open,
  openViewModal,
  setView,
  onDuplicate,
  userId,
  sortField,
  sortOrder,
  currentPage,
  dateRange,
  activeFilters,
}) => {
  const { profile } = useSelector((state) => state.profile);
  const { allStaff } = useSelector((state) => state.staff);

  // Same gating NoteModalNew's view drawer uses for its own Approve/Reject
  // buttons (canApproveOrReject), minus hideApproveRejectButtons — that prop
  // only hides them on the plain "View" open on this page; the kebab's own
  // Approve/Reject items open the drawer with it turned off instead (see
  // openViewModal(row, { showApprovalActions: true }) below).
  const canApprove = (() => {
    const ts = row?.noteId?.templateSettings || {};
    const approvers = Array.isArray(ts.approvers) ? ts.approvers.map(String) : [];
    const restricted = ts.allApprovers === false && approvers.length > 0;
    if (!restricted) return true;
    if (profile?.user?.role === "superadmin") return true;
    return approvers.includes(String(profile?.user?.id));
  })();
  const canApproveOrReject =
    canApprove &&
    (!profile?.user?.type || profile?.user?.role === "admin") &&
    row?.approvalStatus !== "approved" &&
    row?.approvalStatus !== "rejected";
  const { allFacilities } = useSelector((state) => state?.facility);
  const { noteSettings } = useSelector((state) => state?.note);
  const [rowValues, setRowValues] = useState({});
  const [pdfModalOpened, setPdfModalOpened] = useState(false);
  const { classes } = useStyles();
  const getFields = () => {
    const fields = {};
    row?.noteId?.custom?.forEach((customElement) => {
      if ("options" in customElement) {
        let optName =
          customElement?.options?.toLowerCase() == "staff"
            ? "Staff All"
            : customElement?.options;
        let options = getOptions(optName, row?.noteId?.custom, undefined, true);
        // let options = getOptions(customElement?.options, row?.noteId?.custom);
        let tempFields = Object.entries(row?.fields)?.find(
          (value) =>
            customElement?.field === "Client Name"
              ? value?.[0]?.["Patient Name"] && !value?.[0]?.["Client Name"]
                ? value?.[0] === "Patient Name"
                : value?.[0]?.["Patient Name"] && value?.[0]?.["Client Name"]
                ? value?.[0] === "Client Name"
                : value?.[0] === "Patient Name" || value?.[0] === "Client Name"
              : value?.[0] === customElement?.field
          // ||
          // value?.[0] === "Client Name"
        );

        let value = options?.find(
          (val) => val?.value === tempFields?.[1]
        )?.label;
        fields[tempFields?.[0]] = value;
      } else {
        if (
          row?.noteId?.name === "Progress Note" &&
          row?.supportPlan === "yes"
        ) {
          fields["_outcomes"] = row.descriptions;
        } else if (
          row?.noteId?.name === "Essential Support Note" &&
          (row?.essentialSupportToggle === "yes" || true)
        ) {
          fields["_outcomes"] = row.essentialSupport;
        } else if (
          row?.noteId?.name === "Intervention Progress Note" &&
          (row?.interventionPlanToggle === "yes" || true)
        ) {
          fields["_outcomes"] = row.goals;
        }
        let tempFields = Object.entries(row?.fields)?.find(
          (value) => value?.[0] === customElement?.field
        );
        if (tempFields?.[0] !== undefined)
          fields[tempFields?.[0]] = tempFields?.[1];
      }
    });

    const obj = {};
    Object.entries(fields)?.map((v) => {
      if (v?.[0] !== undefined || null) {
        obj[v[0]] = v?.[1] || "-";
      }
    });
    // Multi-client notes (Group Note, Incident Report): resolve multiple client
    // names using getOptions (same lookup as the single-client case) and show
    // "First Name +N more" instead of a raw id list.
    const resolveMultiNames = (ids, optionType) => {
      if (!Array.isArray(ids) || ids.length === 0) return null;
      const options = getOptions(optionType, row?.noteId?.custom, undefined, true);
      const names = ids
        .map((id) => options?.find((opt) => opt?.value === id)?.label)
        .filter(Boolean);
      if (names.length === 0) return null;
      return names.length > 1 ? `${names[0]} +${names.length - 1} more` : names[0];
    };

    // Group Note is NOT multi-client here — the backend splits a multi-client
    // submission into one separate Note per client at create time, so every
    // Group Note document always has a single "Client Name" (the multi-select
    // only exists in the create UI). It falls through to the generic
    // single-id resolution below/above like any other note type.

    // Any note type may store multiple clients/staff under the same
    // "Client Name" / "Staff Name" keys as an array instead of a single id
    // (Incident Report, plus Progress / Essential Support / Intervention
    // Progress / Billing Services). A single selection stays a plain id string
    // and falls through to the single-id resolution above, unchanged.
    if (Array.isArray(row?.fields?.["Client Name"])) {
      const clientLabel = resolveMultiNames(row?.fields?.["Client Name"], "clients");
      if (clientLabel) obj["Client Name"] = clientLabel;
    }
    if (Array.isArray(row?.fields?.["Staff Name"])) {
      const staffLabel = resolveMultiNames(row?.fields?.["Staff Name"], "staff all");
      if (staffLabel) obj["Staff Name"] = staffLabel;
    }

    // Multiple staff on one note (e.g. a future multi-staff Group Note):
    // render "First Staff Name +N" instead of a single name. Falls back to
    // the existing single "Staff Name" resolution above when staffIds isn't set.
    if (Array.isArray(row?.fields?.staffIds) && row?.fields?.staffIds?.length > 0) {
      const staffOptions = getOptions("staff all", row?.noteId?.custom, undefined, true);
      const names = row.fields.staffIds
        .map((id) => staffOptions?.find((opt) => opt?.value === id)?.label)
        .filter(Boolean);
      if (names.length > 0) {
        obj["Staff Name"] = names[0];
        obj["_staffOverflow"] = names.length > 1 ? names.length - 1 : 0;
      }
    }

    // Care-plan rows for STUDIO templates. The loop above only fills _outcomes
    // for the three built-in types, matched by note-type NAME — a studio
    // template documents the very same care plan under whatever name its
    // author chose, so its Description cell fell through to an empty
    // Parameters() and showed nothing. Key off the saved toggles instead of the
    // name, and concatenate: a studio care plan field can document BOTH
    // Support Plan outcomes and Essential Supports on one note (carePlanParts).
    if (!Array.isArray(obj["_outcomes"]) || !obj["_outcomes"].length) {
      const carePlanRows = [
        ...(row?.supportPlan === "yes" ? row?.descriptions || [] : []),
        ...(row?.essentialSupportToggle === "yes" ? row?.essentialSupport || [] : []),
      ].filter((r) => r?.description);
      if (carePlanRows.length) obj["_outcomes"] = carePlanRows;
    }

    // ✅ Add note type here
    obj["_noteType"] = row?.noteId?.name || "-";
    obj["_isInterventionPlan"] = row?.interventionPlanToggle || "-";
    setRowValues(obj);
    return Object.entries(fields);
  };
  useEffect(() => {
    getFields();
  }, [row, allStaff]);

  // functions
  const remove = async (canDelete) => {
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });

    if (willDelete) removeNote(row, userId, () => {}, sortField, sortOrder, false, currentPage, dateRange, activeFilters);
  };

  function handleRowClick(event) {
    setView(true);
    openViewModal(row);
  }
  const Parameters = ({ parameters }) => {
    if (parameters?.length > 1)
      return (
        <Text className={classes.cell}>
          {`${
            parameters?.[0]?.description?.length > 50
              ? `${parameters?.[0]?.description?.slice(0, 50)}...`
              : parameters?.[0]?.description
          }`}
          <Box className={classes.parametersContainer}>
            + {parameters?.length - 1}
          </Box>
        </Text>
      );

    return (
      <Text className={classes.cell}>
        {`${
          parameters?.[0]?.description
            ? parameters[0].description.length > 50
              ? `${parameters[0].description.slice(0, 50)}...`
              : parameters[0].description
            : "-"
        }`}
      </Text>
    );
  };

  const InterventionParameters = ({ parameters }) => {
    const truncate = (text) =>
      text?.length > 50 ? `${text.slice(0, 50)}...` : text;

    const mappedParams = [
      { label: "Behavior", value: parameters?.[0]?.behaviorDescription },
      { label: "Plan", value: parameters?.[0]?.planDescription },
      { label: "Response", value: parameters?.[0]?.responseDescription },
    ].filter((item) => item.value);

    // 👉 case when more than 1 parameter object exists
    if (parameters?.length > 1) {
      return (
        <Text className={classes.cell} style={{ display: "flex", gap: 8 }}>
          {mappedParams.length > 0 ? (
            mappedParams.map((p, idx) => (
              <Text key={idx} style={{ fontSize: 14 }}>
                <strong>{p.label}:</strong> {truncate(p.value)}
              </Text>
            ))
          ) : (
            <Text>-</Text>
          )}
          <Box className={classes.parametersContainer}>
            + {parameters.length - 1}
          </Box>
        </Text>
      );
    }

    // 👉 case when only 1 parameter object exists
    return (
      <div
        className={classes.cell}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "flex-start",
        }}
      >
        {mappedParams.length > 0 ? (
          mappedParams.map((p, idx) => (
            <Text key={idx} style={{ fontSize: 14 }}>
              <strong>{p.label}:</strong> {truncate(p.value)}
            </Text>
          ))
        ) : (
          <Text>-</Text>
        )}
      </div>
    );
  };

  const bg = index % 2 === 0;


  return (
    <>
      <tr
        style={{ cursor: "pointer" }}
        className={bg ? classes.bg2 : classes.bg1}
        key={index}
      >
        <td className={classes.textMuted} onClick={handleRowClick} style={{ textTransform: "uppercase" }}>
          {row?.id?.slice(-6)}
        </td>
        <td className={classes.textDark} onClick={handleRowClick}>{row?.noteId?.name}</td>
        <td className={classes.textMuted} onClick={handleRowClick}>
          {rowValues?.["Staff Name"] || "-"}
          {rowValues?._staffOverflow > 0 && (
            <Box component="span" className={classes.staffOverflow} ml={6}>
              +{rowValues._staffOverflow}
            </Box>
          )}
        </td>
        <td className={classes.textMuted} onClick={handleRowClick}>
          {rowValues?.["Date"] && moment(rowValues["Date"]).isValid()
            ? moment(rowValues["Date"]).format("MM-DD-YYYY")
            : "-"}
        </td>

        <td className={classes.textMuted} onClick={handleRowClick}>
          {rowValues?.["Time"] && moment(rowValues["Time"], "HH:mm:ss").isValid()
            ? moment(rowValues["Time"], "HH:mm:ss").format("hh:mm:ss a")
            : "-"}
        </td>
        <td className={classes.textMuted} onClick={handleRowClick}>{rowValues?.["Facility Name"] || "-"}</td>
        <td className={classes.textDark} onClick={handleRowClick}>
          {rowValues?.[noteType_Client] || rowValues?.["Patient Name"] || "-"}
        </td>
        <td className={classes.textMuted} onClick={handleRowClick}>
          {rowValues?._noteType === "Intervention Progress Note" ? (
            <>
              {rowValues?._isInterventionPlan === "yes" ? (
                <InterventionParameters parameters={rowValues["_outcomes"]} />
              ) : rowValues?.["Description"] ? (
                <Tooltip
                  w={300}
                  multiline
                  withArrow
                  label={rowValues?.["Description"]}
                >
                  <span>
                    {rowValues?.["Description"]?.length > 50
                      ? `${rowValues?.["Description"]?.slice(0, 140)}...`
                      : rowValues?.["Description"]}
                  </span>
                </Tooltip>
              ) : (
                "-"
              )}
            </>
          ) : rowValues?._noteType !== "Intervention Progress Note" ? (
            //  <Parameters parameters={rowValues["_outcomes"]} />
            rowValues?.["Description"] &&
            rowValues?.["Description"].trim() !== "" &&
            rowValues["Description"].trim() !== "-" ? (
              `${rowValues["Description"].slice(0, 140)}...`
            ) : (
              <Parameters parameters={rowValues["_outcomes"]} />
            )
          ) : rowValues?.["Description"] ? (
            <Tooltip
              w={300}
              multiline
              withArrow
              label={rowValues?.["Description"]}
            >
              <span>
                {rowValues?.["Description"]?.length > 50
                  ? `${rowValues?.["Description"]?.slice(0, 140)}...`
                  : rowValues?.["Description"]}
              </span>
            </Tooltip>
          ) : (
            "-"
          )}

          {/* {rowValues?._noteType === "Intervention Progress Note" ? ((
            <InterventionParameters parameters={rowValues["_outcomes"]} />
          ) : rowValues?.["Description"] ? (
            <Tooltip
              w={300}
              multiline
              withArrow
              label={`${rowValues?.["Description"]}`}
            >
              <span>
                {rowValues?.["Description"]?.length > 50
                  ? `${rowValues?.["Description"]?.slice(0, 140)}...`
                  : rowValues?.["Description"]}
              </span>
            </Tooltip>
          ) : (
            "-"
          )

          ) : (
            <Parameters parameters={rowValues["_outcomes"]} />
          ) : rowValues?.["Description"] ? (
            <Tooltip
              w={300}
              multiline
              withArrow
              label={`${rowValues?.["Description"]}`}
            >
              <span>
                {rowValues?.["Description"]?.length > 50
                  ? `${rowValues?.["Description"]?.slice(0, 140)}...`
                  : rowValues?.["Description"]}
              </span>
            </Tooltip>
          ) : (
            "-"
          ) } */}
          {/* {rowValues?._outcomes ? (
            <Parameters parameters={rowValues["_outcomes"]} />
          ) : rowValues?.["Description"] ? (
            <Tooltip
              w={300}
              multiline
              withArrow
              label={`${rowValues?.["Description"]}`}
            >
              <span>
                {rowValues?.["Description"]?.length > 50
                  ? `${rowValues?.["Description"]?.slice(0, 140)}...`
                  : rowValues?.["Description"]}
              </span>
            </Tooltip>
          ) : (
            "-"
          )} */}
        </td>
        <td>
          <Menu shadow="md" position="bottom-end" withinPortal zIndex={"1500"}>
            <Menu.Target>
              <ActionIcon className={classes.kebab}>
                <BiDotsVerticalRounded id="vertical-dots" size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                icon={<IconEye size={16} />}
                onClick={() => {
                  setView(true);
                  openViewModal(row);
                  getAllFacilitiesModal("withDeleted");
                }}
              >
                View
              </Menu.Item>
              {canApproveOrReject && (
                <>
                  <Menu.Item
                    icon={<IconCheck size={16} />}
                    onClick={() => {
                      setView(true);
                      openViewModal(row, { showApprovalActions: true });
                      getAllFacilitiesModal("withDeleted");
                    }}
                  >
                    Approve
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconX size={16} />}
                    onClick={() => {
                      setView(true);
                      openViewModal(row, { showApprovalActions: true });
                      getAllFacilitiesModal("withDeleted");
                    }}
                  >
                    Reject
                  </Menu.Item>
                </>
              )}
              <Menu.Item
                // disabled={
                //   profile?.user?.type !== "staff" ||
                //   (profile?.user?.type === "staff" &&
                //     profile?.user?.role === "admin") ||
                //   row?.fields?.["Staff Name"] === profile?.user?.id
                //     ? false
                //     : true
                // }
                // disabled={(() => {
                //   const createdAtDate = new Date(row?.createdAt);
                //   const now = new Date();

                //   const isSameDay =
                //     createdAtDate.getUTCFullYear() === now.getUTCFullYear() &&
                //     createdAtDate.getUTCMonth() === now.getUTCMonth() &&
                //     createdAtDate.getUTCDate() === now.getUTCDate();

                //   if (isSameDay) {
                //     return false;
                //   }

                //   return !(
                //     profile?.user?.type !== "staff" ||
                //     (profile?.user?.type === "staff" &&
                //       profile?.user?.role === "admin") ||
                //     row?.fields?.["Staff Name"] === profile?.user?.id
                //   );
                // })()}

        disabled={(() => {
                  const createdAtDate = new Date(row?.createdAt);
                  const now = new Date();

                  if (
                    profile?.user?.type !== "staff" ||
                    (profile?.user?.type === "staff" &&
                      profile?.user?.role === "admin")
                  ) {
                    return false;
                  }

                  if (
                    noteSettings?.general?.lockAfterApproval &&
                    row?.approvalStatus === "approved"
                  ) {
                    return true;
                  }

                  const isOwnRecord =
                    row?.fields?.["Staff Name"] === profile?.user?.id;
                  if (!isOwnRecord) {
                    return true;
                  }


                  const facilityId = row?.fields?.["Facility Name"];

                  const facility = allFacilities?.facilities?.find(
                    (f) => f?._id === facilityId || f?.id === facilityId
                  );

                  const editTimeSetting = facility?.staffnoteEditTime || "1d";

                  if (editTimeSetting === "forever") {
                    return false;
                  }

                  const allowedHours = editTimeSetting === "3d" ? 72 : 24;

                  const diffMs = now - createdAtDate;
                  const diffHours = diffMs / (1000 * 60 * 60);

                  return diffHours > allowedHours;
                })()}

                icon={<IconPencil size={16} />}
                onClick={() => {
                  open(row);
                  getAllFacilitiesModal("withDeleted");
                }}


              >
                Edit
              </Menu.Item>
              <Menu.Item
                icon={<IconCopy size={16} />}
                onClick={() => {
                  onDuplicate?.(row);
                  getAllFacilitiesModal("withDeleted");
                }}
              >
                Duplicate
              </Menu.Item>
              <Menu.Item
                icon={<IconFileText size={16} />}
                onClick={() => setPdfModalOpened(true)}
              >
                Download PDF
              </Menu.Item>
              <Menu.Item
                color="red"
                icon={<IconTrash size={16} />}
                onClick={() => remove(row?.canDelete)}
                // disabled={
                //   profile?.user?.type !== "staff" ||
                //   (profile?.user?.type === "staff" &&
                //     profile?.user?.role === "admin") ||
                //   row?.fields?.["Staff Name"] === profile?.user?.id
                //     ? false
                //     : true
                // }
                // disabled={(() => {
                //   const createdAtDate = new Date(row?.createdAt);
                //   const now = new Date();

                //   const isSameDay =
                //     createdAtDate.getUTCFullYear() === now.getUTCFullYear() &&
                //     createdAtDate.getUTCMonth() === now.getUTCMonth() &&
                //     createdAtDate.getUTCDate() === now.getUTCDate();

                //   if (isSameDay) {
                //     return false;
                //   }

                //   return !(
                //     profile?.user?.type !== "staff" ||
                //     (profile?.user?.type === "staff" &&
                //       profile?.user?.role === "admin") ||
                //     row?.fields?.["Staff Name"] === profile?.user?.id
                //   );
                // })()}
                disabled={(() => {
                  const createdAtDate = new Date(row?.createdAt);
                  const now = new Date();

                  if (
                    profile?.user?.type !== "staff" ||
                    (profile?.user?.type === "staff" &&
                      profile?.user?.role === "admin")
                  ) {
                    return false;
                  }

                  if (
                    noteSettings?.general?.lockAfterApproval &&
                    row?.approvalStatus === "approved"
                  ) {
                    return true;
                  }

                  const isOwnRecord =
                    row?.fields?.["Staff Name"] === profile?.user?.id;
                  if (!isOwnRecord) {
                    return true;
                  }

                  const facilityId = row?.fields?.["Facility Name"];

                  const facility = allFacilities?.facilities?.find(
                    (f) => f?._id === facilityId || f?.id === facilityId
                  );

                  const editTimeSetting = facility?.staffnoteEditTime || "1d";

                  if (editTimeSetting === "forever") {
                    return false;
                  }

                  const allowedHours = editTimeSetting === "3d" ? 72 : 24;

                  const diffMs = now - createdAtDate;
                  const diffHours = diffMs / (1000 * 60 * 60);

                  return diffHours > allowedHours;
                })()}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </td>
      </tr>
      <SingleNotePdfModal
        noteId={row?.id}
        opened={pdfModalOpened}
        onClose={() => setPdfModalOpened(false)}
      />
    </>
  );
};

const NoteTableRowDashboard = ({ row, index, open, setView }) => {
  const { classes } = useStyles();
  const { allPatients } = useSelector((state) => state?.patient);
  const { allFacilities } = useSelector((state) => state?.facility);
  const { staff } = useSelector((state) => state?.staff);

  const [rowValues, setRowValues] = useState({});

  const getFields = () => {
    const fields = {};
    row?.noteId?.custom?.forEach((customElement) => {
      if ("options" in customElement) {
        let options = getOptions(customElement?.options, row?.noteId?.custom);
        let tempFields = Object.entries(row?.fields)?.find(
          (value) => value?.[0] === customElement?.field
        );

        let value = options?.find(
          (val) => val?.value === tempFields?.[1]
        )?.label;
        fields[tempFields?.[0]] = value;
      } else {
        if (
          row?.noteId?.name === "Progress Note" &&
          row?.supportPlan === "yes"
        ) {
          fields["_outcomes"] = row.descriptions;
        } else if (
          row?.noteId?.name === "Essential Support Note" &&
          (row?.essentialSupportToggle === "yes" || true)
        ) {
          fields["_outcomes"] = row.essentialSupport;
        } else if (
          row?.noteId?.name === "Intervention Progress Note" &&
          (row?.interventionPlanToggle === "yes" || true)
        ) {
          fields["_outcomes"] = row.goals;
        }
        let tempFields = Object.entries(row?.fields)?.find(
          (value) => value?.[0] === customElement?.field
        );
        if (tempFields?.[0] !== undefined)
          fields[tempFields?.[0]] = tempFields?.[1];
      }
    });

    const obj = {};
    Object.entries(fields)?.map((v) => {
      if (v?.[0] !== undefined || null) {
        obj[v[0]] = v?.[1] || "-";
      }
    });
    // Same studio care-plan fallback as NoteTableRow above — the dashboard
    // table renders the identical Description cell.
    if (!Array.isArray(obj["_outcomes"]) || !obj["_outcomes"].length) {
      const carePlanRows = [
        ...(row?.supportPlan === "yes" ? row?.descriptions || [] : []),
        ...(row?.essentialSupportToggle === "yes" ? row?.essentialSupport || [] : []),
      ].filter((r) => r?.description);
      if (carePlanRows.length) obj["_outcomes"] = carePlanRows;
    }

    obj["_noteType"] = row?.noteId?.name || "-";
    obj["_isInterventionPlan"] = row?.interventionPlanToggle || "-";
    setRowValues(obj);
    return Object.entries(fields);
  };
  function handleRowClick(event) {
    setView(true);
    open(row);
  }
  useEffect(() => {
    getFields();
  }, [row, allPatients, allFacilities?.facilities, staff?.staffs]);

  // const Parameters = ({ parameters }) => {
  //   if (parameters?.length > 1)
  //     return (
  //       <Text
  //         className={classes.cell}
  //         style={{ display: "flex", alignItems: "center", gap: 20 }}
  //       >
  //         {`${
  //           parameters?.[0]?.description?.length > 15
  //             ? `${parameters?.[0]?.description?.slice(0, 15)}...`
  //             : parameters?.[0]?.description
  //         }`}
  //         <div
  //           style={{
  //             background: "var(--app-surface-2)",
  //             borderRadius: 5,
  //             padding: 5,
  //             width: 50,
  //             color: "#A8A8A8",
  //             fontSize: 14,
  //           }}
  //         >
  //           + {parameters?.length - 1}
  //         </div>
  //       </Text>
  //     );

  //   return (
  //     <Text className={classes.cell}>
  //       {`${
  //         parameters?.[0]?.description?.length > 15
  //           ? `${parameters?.[0]?.description?.slice(0, 15)}...`
  //           : parameters?.[0]?.description
  //       }`}
  //     </Text>
  //   );
  // };
  const Parameters = ({ parameters }) => {
    if (parameters?.length > 1)
      return (
        <Text className={classes.cell}>
          {`${
            parameters?.[0]?.description?.length > 50
              ? `${parameters?.[0]?.description?.slice(0, 50)}...`
              : parameters?.[0]?.description
          }`}
          <Box className={classes.parametersContainer}>
            + {parameters?.length - 1}
          </Box>
        </Text>
      );

    return (
      <Text className={classes.cell}>
        {`${
          parameters?.[0]?.description
            ? parameters[0].description.length > 50
              ? `${parameters[0].description.slice(0, 50)}...`
              : parameters[0].description
            : "-"
        }`}
      </Text>
    );
  };
  const InterventionParameters = ({ parameters }) => {
    const truncate = (text) =>
      text?.length > 50 ? `${text.slice(0, 50)}...` : text;

    const mappedParams = [
      { label: "Behavior", value: parameters?.[0]?.behaviorDescription },
      { label: "Plan", value: parameters?.[0]?.planDescription },
      { label: "Response", value: parameters?.[0]?.responseDescription },
    ].filter((item) => item.value);

    // 👉 case when more than 1 parameter object exists
    if (parameters?.length > 1) {
      return (
        <Text className={classes.cell} style={{ display: "flex", gap: 8 }}>
          {mappedParams.length > 0 ? (
            mappedParams.map((p, idx) => (
              <Text key={idx} style={{ fontSize: 14 }}>
                <strong>{p.label}:</strong> {truncate(p.value)}
              </Text>
            ))
          ) : (
            <Text>-</Text>
          )}
          <Box className={classes.parametersContainer}>
            + {parameters.length - 1}
          </Box>
        </Text>
      );
    }

    // 👉 case when only 1 parameter object exists
    return (
      <div
        className={classes.cell}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "flex-start",
        }}
      >
        {mappedParams.length > 0 ? (
          mappedParams.map((p, idx) => (
            <Text key={idx} style={{ fontSize: 14 }}>
              <strong>{p.label}:</strong> {truncate(p.value)}
            </Text>
          ))
        ) : (
          <Text>-</Text>
        )}
      </div>
    );
  };

  const bg = index % 2 === 0;
  return (
    <>
      <tr key={index} className={bg ? classes.bg2 : classes.bg1}>
        <td className={classes.textDark} onClick={handleRowClick}>{row?.noteId?.name}</td>
        <td className={classes.textMuted} onClick={handleRowClick}>{rowValues?.["Staff Name"] || "-"}</td>
        <td className={classes.textDark} onClick={handleRowClick}>{rowValues?.[noteType_Client] || "-"}</td>
        <td className={classes.textMuted} onClick={handleRowClick}> {rowValues?.["Facility Name"] || "-"}</td>
        <td className={classes.textMuted} onClick={handleRowClick}>
          {rowValues?.["Date"]
            ? moment(rowValues["Date"]).isValid()
              ? moment(rowValues["Date"]).format("MM-DD-YYYY")
              : rowValues["Date"]
            : "-"}
        </td>
        <td className={classes.textMuted} onClick={handleRowClick}>
          {rowValues?.["Time"] ? (
            <TimeInput
              // readOnly={view}
              key={index}
              size="md"
              variant="unstyled"
              className={classes.fontSizeClass}
              value={formatTime(rowValues?.["Time"])}
            />
          ) : (
            "-"
          )}
        </td>
        <td className={classes.textMuted} onClick={handleRowClick}>
          {rowValues?._noteType === "Intervention Progress Note" ? (
            <>
              {rowValues?._isInterventionPlan === "yes" ? (
                <InterventionParameters parameters={rowValues["_outcomes"]} />
              ) : rowValues?.["Description"] ? (
                <Tooltip
                  w={300}
                  multiline
                  withArrow
                  label={rowValues?.["Description"]}
                >
                  <span>
                    {rowValues?.["Description"]?.length > 50
                      ? `${rowValues?.["Description"]?.slice(0, 140)}...`
                      : rowValues?.["Description"]}
                  </span>
                </Tooltip>
              ) : (
                "-"
              )}
            </>
          ) : rowValues?._noteType !== "Intervention Progress Note" ? (
            // <Parameters parameters={rowValues["_outcomes"]} />
            rowValues?.["Description"] &&
            rowValues?.["Description"].trim() !== "" &&
            rowValues["Description"].trim() !== "-" ? (
              `${rowValues["Description"].slice(0, 140)}...`
            ) : (
              <Parameters parameters={rowValues["_outcomes"]} />
            )
          ) : rowValues?.["Description"] ? (
            <Tooltip
              w={300}
              multiline
              withArrow
              label={rowValues?.["Description"]}
            >
              <span>
                {rowValues?.["Description"]?.length > 50
                  ? `${rowValues?.["Description"]?.slice(0, 140)}...`
                  : rowValues?.["Description"]}
              </span>
            </Tooltip>
          ) : (
            "-"
          )}
        </td>

        <td>
          <MenuDropdown
            isNote={true}
            row={row}
            view={handleRowClick}
            isDashboradMode={true}
            targetClassName={classes.kebab}
          />
        </td>
      </tr>
    </>
  );
};

export default NoteTableRow;
export { NoteTableRowDashboard };
